/**
 * `publishState` as a form field and as a collection column.
 *
 * It is a field rather than another entity view for one reason: an entity view
 * is a tab, and a tab is a thing you have to know to click. A blocked publish
 * has to be on the first screen of the form, above the title, or it is the
 * Firestore console again with extra steps.
 *
 * The property is declared `disabled` rather than `readOnly`. FireCMS swaps a
 * `readOnly` property's component for its own read-only renderer *before* it
 * looks at `Field`, so `readOnly: true` would silently discard this component
 * and print the raw map. `disabled: true` keeps the custom field and still
 * refuses input, which is what a record written by a Cloud Function needs.
 */

import React from "react";
import { FieldProps, PropertyPreviewProps } from "@firecms/core";
import { Typography } from "@firecms/ui";

import { PublishStageChip, PublishStateBanner } from "./PublishStateBanner";
import { readPublishState } from "./publishState";

/**
 * Renders nothing when there is no state to render.
 *
 * A guide that has never been published has no report, and an empty "no
 * publish attempts yet" panel at the top of every new guide's form would be
 * noise on the one screen that must stay uncluttered.
 */
export function PublishStateField({ value }: FieldProps<Record<string, any>>) {
    const state = readPublishState(value);
    if (!state) return null;
    return (
        <div className={"flex flex-col gap-1 w-full"}>
            <PublishStateBanner state={state}/>
        </div>
    );
}

/** The collection column: a badge, or nothing. */
export function PublishStatePreview({ value }: PropertyPreviewProps<Record<string, any>>) {
    const state = readPublishState(value);
    if (!state) {
        return <Typography variant={"caption"} className={"opacity-40"}>—</Typography>;
    }
    return <PublishStageChip state={state}/>;
}
